import React, { useState } from 'react'
import classNames from 'classnames'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { Alert, Card, CardBody, CardFooter, CardHeader, Nav, NavItem, NavLink, Spinner } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft,
  faChevronRight,
  faFileAlt,
  faMapMarkerAlt,
  faPaperPlane,
  faThumbsUp,
  faUser,
  faUserTag
} from '@fortawesome/free-solid-svg-icons'
import ButtonIcon from '../../../common/ButtonIcon'
import handleErrorMessage from '../../../../helpers/errors/handleErrorMessage'
import { registerProvider } from '../../../../helpers/api/marketplace'
import { DOCUMENTS_BY_TYPE, PROVIDER_TYPES } from '../../marketplaceConstants'
import ProviderDocumentsFields from '../ProviderDocumentsFields'
import AccountTypeStep from './AccountTypeStep'
import ProviderDataStep from './ProviderDataStep'
import ContactCoverageStep from './ContactCoverageStep'
import ProviderRegisterSuccess from './ProviderRegisterSuccess'

const STEPS = [
  { id: 1, label: 'Tipo de cuenta', icon: faUserTag },
  { id: 2, label: 'Datos', icon: faUser },
  { id: 3, label: 'Contacto', icon: faMapMarkerAlt },
  { id: 4, label: 'Documentos', icon: faFileAlt },
  { id: 5, label: 'Listo', icon: faThumbsUp }
]

const DOCUMENTS_STEP = 4
const SUCCESS_STEP = 5

const INITIAL_FORM = {
  type: null,
  first_name: '',
  last_name: '',
  dni: 'V',
  state: '',
  city: '',
  legal_name: '',
  trade_name: '',
  rif: 'J',
  legal_rep_name: '',
  legal_rep_dni: 'V',
  legal_rep_phone: '',
  address: '',
  email: '',
  phone: '',
  coverage_zones: [],
  password: '',
  password_confirmation: ''
}

const NATURAL_FIELDS = ['first_name', 'last_name', 'dni', 'state', 'city']
const COMPANY_FIELDS = ['legal_name', 'trade_name', 'rif', 'address', 'legal_rep_name', 'legal_rep_dni', 'legal_rep_phone']
const CONTACT_FIELDS = ['email', 'phone', 'coverage_zones', 'password', 'password_confirmation']

const NAME_PATTERN = /^[\p{L}\s]+$/u
const IDENTIFICATION_PATTERN = /^[A-Z]\d{5,10}$/
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const requireText = (value, label, min = 2) => {
  if (!value?.trim()) return `${label} es obligatorio.`
  if (value.trim().length < min) return `${label} debe tener al menos ${min} caracteres.`
  return null
}

const requireName = (value, label) =>
  requireText(value, label) || (NAME_PATTERN.test(value.trim()) ? null : 'Solo se permiten letras y espacios.')

const requireIdentification = (value, label) =>
  IDENTIFICATION_PATTERN.test(value || '') ? null : `Escribe ${label} completa.`

const requirePhone = (value) =>
  value && isValidPhoneNumber(value) ? null : 'Escribe un número de teléfono válido.'

const validateStep = (step, form, photo, documents) => {
  const errors = {}
  const add = (field, message) => {
    if (message) errors[field] = message
  }

  if (step === 1 && !form.type) {
    add('type', 'Elige cómo te vas a registrar.')
  }

  if (step === 2) {
    add('photo', photo ? null : form.type === PROVIDER_TYPES.company ? 'Sube el logo de la empresa.' : 'Sube una foto de tu rostro.')
    if (form.type === PROVIDER_TYPES.company) {
      add('legal_name', requireText(form.legal_name, 'La razón social'))
      add('trade_name', requireText(form.trade_name, 'El nombre comercial'))
      add('rif', IDENTIFICATION_PATTERN.test(form.rif) ? null : 'Escribe el RIF completo.')
      add('address', requireText(form.address, 'La dirección', 5))
      add('legal_rep_name', requireName(form.legal_rep_name, 'El nombre del representante'))
      add('legal_rep_dni', requireIdentification(form.legal_rep_dni, 'la cédula del representante'))
      add('legal_rep_phone', requirePhone(form.legal_rep_phone))
    } else {
      add('first_name', requireName(form.first_name, 'El nombre'))
      add('last_name', requireName(form.last_name, 'El apellido'))
      add('dni', requireIdentification(form.dni, 'tu cédula'))
      add('state', form.state ? null : 'Elige el estado.')
      add('city', form.city ? null : 'Elige la ciudad.')
    }
  }

  if (step === 3) {
    add('email', EMAIL_PATTERN.test(form.email.trim()) ? null : 'Escribe un correo electrónico válido.')
    add('phone', requirePhone(form.phone))
    add('coverage_zones', form.coverage_zones.length ? null : 'Agrega al menos una zona.')
    add('password', form.password.length >= 8 ? null : 'La contraseña debe tener al menos 8 caracteres.')
    add(
      'password_confirmation',
      form.password_confirmation && form.password_confirmation === form.password ? null : 'Las contraseñas no coinciden.'
    )
  }

  if (step === DOCUMENTS_STEP) {
    DOCUMENTS_BY_TYPE[form.type].required.forEach((type) => {
      add(`documents.${type}`, documents[type] ? null : 'Este documento es obligatorio.')
    })
  }

  return errors
}

const stepForField = (field) => {
  if (field === 'type') return 1
  if (field === 'photo' || NATURAL_FIELDS.includes(field) || COMPANY_FIELDS.includes(field)) return 2
  if (CONTACT_FIELDS.includes(field)) return 3
  return DOCUMENTS_STEP
}

const documentErrors = (errors) =>
  Object.entries(errors).reduce((acc, [field, message]) => {
    if (field.startsWith('documents.')) acc[field.replace('documents.', '')] = message
    return acc
  }, {})

const ProviderRegisterWizard = () => {
  const [step, setStep] = useState(1)
  const [form, setForm] = useState(INITIAL_FORM)
  const [photo, setPhoto] = useState(null)
  const [documents, setDocuments] = useState({})
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const clearError = (field) =>
    setErrors((prev) => {
      if (!prev[field]) return prev
      const { [field]: removed, ...rest } = prev
      return rest
    })

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    clearError(field)
    if (field === 'password') clearError('password_confirmation')
  }

  const handleTypeChange = (type) => {
    if (type !== form.type) setDocuments({})
    handleChange('type', type)
  }

  const handleDocumentChange = (type, file) => {
    setDocuments((prev) => ({ ...prev, [type]: file }))
    clearError(`documents.${type}`)
  }

  const buildPayload = () => {
    const fields = form.type === PROVIDER_TYPES.company ? COMPANY_FIELDS : NATURAL_FIELDS
    const payload = { type: form.type, photo }
    ;[...fields, ...CONTACT_FIELDS].forEach((field) => {
      payload[field] = typeof form[field] === 'string' ? form[field].trim() : form[field]
    })
    payload.password = form.password
    payload.password_confirmation = form.password_confirmation
    payload.documents = Object.fromEntries(Object.entries(documents).filter(([, file]) => file))
    return payload
  }

  const submit = async () => {
    setSubmitting(true)
    try {
      await registerProvider(buildPayload())
      setStep(SUCCESS_STEP)
    } catch (error) {
      const fieldErrors = error?.data?.errors
      if (fieldErrors) {
        const mapped = Object.fromEntries(
          Object.entries(fieldErrors).map(([field, messages]) => [field, [].concat(messages)[0]])
        )
        setErrors(mapped)
        setStep(Math.min(...Object.keys(mapped).map(stepForField)))
      } else {
        handleErrorMessage(error?.data || error)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleNext = () => {
    const stepErrors = validateStep(step, form, photo, documents)
    if (Object.keys(stepErrors).length) {
      setErrors((prev) => ({ ...prev, ...stepErrors }))
      return
    }
    if (step === DOCUMENTS_STEP) {
      submit()
      return
    }
    setStep(step + 1)
  }

  const goTo = (target) => {
    if (target < step && step !== SUCCESS_STEP && !submitting) setStep(target)
  }

  const hasErrors = Object.keys(errors).some((field) => stepForField(field) === step)

  return (
    <Card className="theme-wizard">
      <CardHeader className="bg-light">
        <Nav className="justify-content-center flex-nowrap">
          {STEPS.map((item) => (
            <NavItem key={item.id}>
              <NavLink
                className={classNames('font-weight-semi-bold', {
                  'done cursor-pointer': step > item.id && step !== SUCCESS_STEP,
                  done: step === SUCCESS_STEP && item.id < SUCCESS_STEP,
                  active: step === item.id
                })}
                onClick={() => goTo(item.id)}
              >
                <span className="nav-item-circle-parent">
                  <span className="nav-item-circle">
                    <FontAwesomeIcon icon={item.icon} />
                  </span>
                </span>
                <span className="d-none d-md-block mt-1 fs--1">{item.label}</span>
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </CardHeader>
      <CardBody className="fs--1 font-weight-normal px-md-5 pt-4 pb-3">
        {step === 1 && <AccountTypeStep value={form.type} onChange={handleTypeChange} error={errors.type} />}
        {step === 2 && (
          <ProviderDataStep
            form={form}
            photo={photo}
            errors={errors}
            onChange={handleChange}
            onPhotoChange={(file) => {
              setPhoto(file)
              clearError('photo')
            }}
          />
        )}
        {step === 3 && <ContactCoverageStep form={form} errors={errors} onChange={handleChange} />}
        {step === DOCUMENTS_STEP && (
          <>
            <Alert color="info" className="fs--1">
              Solo el equipo de Homelan ve estos documentos. Los usamos para verificar tu identidad antes de mostrar tu perfil en el Marketplace.
            </Alert>
            <ProviderDocumentsFields
              providerType={form.type}
              files={documents}
              errors={documentErrors(errors)}
              onChange={handleDocumentChange}
            />
          </>
        )}
        {step === SUCCESS_STEP && <ProviderRegisterSuccess email={form.email.trim()} />}
        {hasErrors && step !== 1 && (
          <p className="text-danger fs--1 mb-0">Revisa los campos marcados para continuar.</p>
        )}
      </CardBody>
      {step !== SUCCESS_STEP && (
        <CardFooter className="px-md-5 bg-light d-flex">
          <ButtonIcon
            color="link"
            icon={faChevronLeft}
            iconAlign="left"
            transform="down-1 shrink-4"
            className={classNames('px-0 font-weight-semi-bold', { 'd-none': step === 1 })}
            onClick={() => setStep(step - 1)}
            disabled={submitting}
          >
            Anterior
          </ButtonIcon>
          <ButtonIcon
            color="primary"
            className="ml-auto px-5"
            icon={step === DOCUMENTS_STEP ? faPaperPlane : faChevronRight}
            iconAlign="right"
            transform="down-1 shrink-4"
            onClick={handleNext}
            disabled={submitting}
          >
            {submitting ? <Spinner size="sm" /> : step === DOCUMENTS_STEP ? 'Enviar registro' : 'Siguiente'}
          </ButtonIcon>
        </CardFooter>
      )}
    </Card>
  )
}

export default ProviderRegisterWizard
